/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import type { RendererProps } from './types'

import { humaniseSegment, labelForPath } from '../diff/fieldLabels'
import type { NormalizedChange } from '../diff/normalize'
import { groupBy } from '../diff/normalize'
import { formatValueText, numericDelta } from '../diff/valueFormatters'
import { ChangeList, Section } from '../primitives'

/**
 * Virtual Setpoint: temperature-first.
 *
 * Magnitude is what matters here — "4.0 °C → 2.0 °C" is far less useful without the "▼ 2.0" that
 * says how big the change was — so the delta is shown explicitly, with a sign as well as an arrow
 * so direction never depends on colour.
 */

const TEMPERATURE_FIELDS = ['setPoint', 'minTemperature', 'maxTemperature', 'temperature']

/** octopus keys each zone's setpoint as `zone1VirtualSetPoint`, `zone2VirtualSetPoint`, … */
const ZONE_SETPOINT = /^zone(\d+)VirtualSetPoint$/i

const zoneSetPointOf = (change: NormalizedChange): RegExpExecArray | null =>
  change.segments.length === 1 ? ZONE_SETPOINT.exec(change.segments[0]) : null

const isTemperature = (change: NormalizedChange): boolean =>
  (TEMPERATURE_FIELDS.includes(change.segments[change.segments.length - 1]) || zoneSetPointOf(change) !== null) &&
  (typeof change.before === 'number' || typeof change.after === 'number')

/** `zones.0.setPoint` or `zone1VirtualSetPoint` → `Zone 1`; anything else groups under General. */
const zoneOf = (change: NormalizedChange): string => {
  const zoneSetPoint = zoneSetPointOf(change)
  if (zoneSetPoint) return `Zone ${zoneSetPoint[1]}`

  if (change.segments[0] === 'zones' && change.segments.length >= 2) {
    const index = Number(change.segments[1])
    return Number.isInteger(index) ? `Zone ${String(index + 1)}` : humaniseSegment(change.segments[1])
  }
  return 'General'
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Unpack a whole-record entry into one change per field, and label the zone setpoints.
 *
 * octopus records a create as a single entry at the empty path whose replacement is the whole
 * record — `{ o: 1, p: '', v: null, r: { zone1VirtualSetPoint: 2 } }` — which would otherwise read
 * as "Record: 1 field". Several zones can be set in one change, so each key becomes its own row.
 * A key equal on both sides is not a change and is dropped.
 */
const expandRecord = (changes: NormalizedChange[]): NormalizedChange[] =>
  changes
    .flatMap((change) => {
      if (change.segments.length > 0 || (!isRecord(change.before) && !isRecord(change.after))) return [change]

      const before = isRecord(change.before) ? change.before : {}
      const after = isRecord(change.after) ? change.after : {}

      const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
        .filter((field) => before[field] !== after[field])
        .map((field): NormalizedChange => {
          const inBefore = field in before
          const inAfter = field in after
          return {
            key: `${change.key}-${field}`,
            state: inBefore && inAfter ? 'MODIFIED' : inAfter ? 'ADDED' : 'REMOVED',
            label: labelForPath(field),
            path: field,
            segments: [field],
            before: inBefore ? before[field] : undefined,
            after: inAfter ? after[field] : undefined,
          }
        })

      // Nothing to unpack still renders the original entry rather than an empty section.
      return fields.length > 0 ? fields : [change]
    })
    .map((change) => {
      const zoneSetPoint = zoneSetPointOf(change)
      return zoneSetPoint ? { ...change, label: `Zone${zoneSetPoint[1]} Virtual Setpoint` } : change
    })
    .map(asAddOrRemove)

const isBlank = (value: unknown): boolean => value === null || value === undefined

/**
 * octopus clears a zone's setpoint as a replace with `null` — `{ o: 1, p: 'zone3VirtualSetPoint',
 * v: 1, r: null }` — and can set an unset one as a replace from `null`. Those are a removal and an
 * addition, and read as such rather than as "1.0 °C → —" or "— → 1.0 °C".
 */
const asAddOrRemove = (change: NormalizedChange): NormalizedChange => {
  if (change.state !== 'MODIFIED') return change
  if (isBlank(change.after) && !isBlank(change.before)) return { ...change, state: 'REMOVED', after: undefined }
  if (isBlank(change.before) && !isBlank(change.after)) return { ...change, state: 'ADDED', before: undefined }
  return change
}

/** Prefix the zone onto a label that does not already name it, for the flat framed lists. */
const withZoneLabel = (change: NormalizedChange): NormalizedChange => {
  const zone = zoneOf(change)
  return zone === 'General' || zoneSetPointOf(change) ? change : { ...change, label: `${zone} — ${change.label}` }
}

/**
 * @param unit the user's temperature unit, appended to values and to the delta. Values arrive
 * already in the user's unit from the source system, so this labels rather than converts.
 */
const TemperatureRow: React.FC<{ change: NormalizedChange; unit: string }> = ({ change, unit }) => {
  const delta = numericDelta(change.before, change.after, unit)
  const withUnit = (value: unknown) =>
    typeof value === 'number' ? `${value.toFixed(1)} ${unit}` : formatValueText(value)

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2">
      <dt className="text-sm" title={change.path}>
        {change.label}:
      </dt>
      <dd className="flex items-center gap-2 text-sm">
        {change.state !== 'ADDED' && <span className="text-base-content/60">{withUnit(change.before)}</span>}
        {change.state === 'MODIFIED' && (
          <span aria-hidden className="text-base-content/40">
            →
          </span>
        )}
        {change.state !== 'REMOVED' && <span className="font-semibold">{withUnit(change.after)}</span>}
        {delta && (
          <span
            className={`rounded-field px-1.5 py-0.5 text-xs font-medium ${
              delta.direction === 'down' ? 'bg-accent/10 text-accent' : 'bg-warning/15 text-warning-content'
            }`}
          >
            <span className="sr-only">{delta.label}</span>
            <span aria-hidden>{delta.text}</span>
          </span>
        )}
      </dd>
    </div>
  )
}

const Changes: React.FC<{ changes: NormalizedChange[]; unit: string }> = ({ changes, unit }) => {
  const temperatures = changes.filter(isTemperature)
  const others = changes.filter((c) => !isTemperature(c))

  return (
    <div className="flex flex-col gap-3">
      {temperatures.length > 0 && (
        <dl className="divide-y divide-base-200 rounded-box border border-base-300">
          {temperatures.map((change) => (
            <TemperatureRow key={change.key} change={change} unit={unit} />
          ))}
        </dl>
      )}
      {others.length > 0 && <ChangeList changes={others} />}
    </div>
  )
}

/** Same framing as the shared create / delete card, which this renderer opts out of. */
const SNAPSHOT_FRAMING = {
  create: { heading: 'Created with these values', tone: 'success' },
  delete: { heading: 'Deleted — last known values', tone: 'error' },
} as const

export const VirtualSetPointRenderer: React.FC<RendererProps> = ({ audit, changes }) => {
  // Unit is not carried on the audit record; the source system stores the company's configured
  // unit, so the value is labelled rather than converted. Celsius is the platform default.
  const unit = '°C'

  const expanded = expandRecord(changes)
  const framing =
    audit.changeType === 'create' || audit.changeType === 'delete' ? SNAPSHOT_FRAMING[audit.changeType] : null

  // A create or delete sets every zone at once, so one framed list with the zone in each label reads
  // better than a section per zone holding a single row. Zone setpoint labels already name the zone.
  if (framing) {
    const rows = expanded.map(withZoneLabel)

    return (
      <div className="flex flex-col gap-3">
        <Section title={framing.heading} tone={framing.tone} count={rows.length}>
          <Changes changes={rows} unit={unit} />
        </Section>
      </div>
    )
  }

  // A setpoint added or removed within an update is framed exactly like a created or deleted record,
  // below the zones that actually changed.
  const added = expanded.filter((change) => change.state === 'ADDED').map(withZoneLabel)
  const removed = expanded.filter((change) => change.state === 'REMOVED').map(withZoneLabel)
  const zones = groupBy(
    expanded.filter((change) => change.state === 'MODIFIED'),
    zoneOf
  )

  return (
    <div className="flex flex-col gap-3">
      {Array.from(zones.entries()).map(([zone, zoneChanges]) => (
        <Section key={zone} title={zone} count={zoneChanges.length}>
          <Changes changes={zoneChanges} unit={unit} />
        </Section>
      ))}
      {added.length > 0 && (
        <Section title={SNAPSHOT_FRAMING.create.heading} tone={SNAPSHOT_FRAMING.create.tone} count={added.length}>
          <Changes changes={added} unit={unit} />
        </Section>
      )}
      {removed.length > 0 && (
        <Section title={SNAPSHOT_FRAMING.delete.heading} tone={SNAPSHOT_FRAMING.delete.tone} count={removed.length}>
          <Changes changes={removed} unit={unit} />
        </Section>
      )}
    </div>
  )
}
